/**
 * Widget API client for the Nova chat widget.
 * Handles HMAC-signed requests, PII scrubbing, and HTTPS enforcement.
 */

import { scrubPII } from '../pii';
import type {
  ApiClientConfig,
  ChatRequest,
  ChatResponse,
  UserTicket,
  TicketUpdate,
  TicketFeedbackRequest,
  ChatbotConfigResponse,
} from './types';

export class WidgetApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    if (!config.apiUrl.startsWith('https://') && !config.apiUrl.startsWith('http://localhost')) {
      throw new Error('Nova SDK requires HTTPS for API communication');
    }
    this.config = config;
  }

  /**
   * Send a chat message. PII is scrubbed from the message and display name
   * before transmission.
   */
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    const scrubbed: ChatRequest = {
      ...request,
      message: scrubPII(request.message),
      externalUserDisplayName: request.externalUserDisplayName
        ? scrubPII(request.externalUserDisplayName)
        : undefined,
    };
    return this.post<ChatResponse>('/sdk/v1/chat', scrubbed);
  }

  /**
   * Fetch all tickets for the configured external user.
   */
  async getTickets(): Promise<{ data: { items: UserTicket[] } }> {
    return this.get<{ data: { items: UserTicket[] } }>(
      `/sdk/v1/tickets?external_user_id=${encodeURIComponent(this.config.externalUserId)}`,
    );
  }

  /**
   * Fetch ticket updates since a given ISO timestamp.
   */
  async getTicketUpdates(since: string): Promise<{ data: { items: TicketUpdate[] } }> {
    return this.get<{ data: { items: TicketUpdate[] } }>(
      `/sdk/v1/tickets/updates?external_user_id=${encodeURIComponent(this.config.externalUserId)}&since=${encodeURIComponent(since)}`,
    );
  }

  /**
   * Submit user feedback on a resolved ticket.
   */
  async submitFeedback(
    ticketId: string,
    feedback: TicketFeedbackRequest,
  ): Promise<{ data: { success: boolean } }> {
    return this.post<{ data: { success: boolean } }>(`/sdk/v1/tickets/${ticketId}/feedback`, {
      ...feedback,
      externalUserId: this.config.externalUserId,
    });
  }

  /**
   * Fetch the chatbot configuration (theme, welcome message, categories).
   */
  async getConfig(): Promise<{ data: ChatbotConfigResponse }> {
    return this.get<{ data: ChatbotConfigResponse }>('/sdk/v1/config');
  }

  /**
   * Compute HMAC-SHA256 signature of a request body using the Web Crypto API.
   */
  private async computeHmac(body: string): Promise<string> {
    const encoder = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(this.config.apiSecret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );
    const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(body));
    return Array.from(new Uint8Array(sig))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * Send a GET request with HMAC-signed empty body.
   */
  private async get<T>(path: string): Promise<T> {
    const url = `${this.config.apiUrl}${path}`;
    const hmac = await this.computeHmac('');
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        'X-Nova-Api-Key': this.config.apiKey,
        'X-Nova-Signature': hmac,
      },
    });
    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }
    return res.json() as Promise<T>;
  }

  /**
   * Send a POST request with HMAC-signed JSON body.
   */
  private async post<T>(path: string, body: unknown): Promise<T> {
    const url = `${this.config.apiUrl}${path}`;
    const bodyStr = JSON.stringify(body);
    // Sign empty string: API Gateway REQUEST authorizers don't receive the body,
    // so the auth Lambda always verifies HMAC('') regardless of HTTP method.
    const hmac = await this.computeHmac('');
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Nova-Api-Key': this.config.apiKey,
        'X-Nova-Signature': hmac,
      },
      body: bodyStr,
    });
    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }
    return res.json() as Promise<T>;
  }
}
